import { useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'

import { api } from '../api/client'
import type { ConsoleDashboard, DashboardKind, DashboardSezione, DashboardWidget } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { usePermessiModuli } from '../auth/permessi'
import { Card, ErrorBanner, Spinner, formatDateTime } from './common'
import { Griglia, MISURE, completaGriglia } from './Griglia'
import { CorniceSchermo, SceltaSchermo, altezzaRigaPer, leggiSchermo } from './SchermoRiferimento'
import { CATALOGO, DisegnaWidget, GRUPPI_WIDGET, PER_TIPO, type DefinizioneWidget } from './widget/catalogo'
import { EditorWidget } from './widget/EditorWidget'
import { idNuovo, impacchettaImmagini, widgetDa } from './widget/modello'
import { useAsync } from '../hooks/useAsync'
import { useEagle } from '../hooks/useEagle'
import { t } from '../i18n/lingua'

/**
 * Il motore della dashboard personalizzabile, usato dalla pagina iniziale
 * (`kind: home`) e dal cruscotto di Eagle (`kind: eagle`).
 *
 * Sezioni, e in ogni sezione una griglia di widget dal catalogo. La prima
 * volta si vede la **predefinita** costruita con ciò che la persona può
 * vedere; «Personalizza» la rende sua e la salva sul server. Un
 * amministratore la salva **per il tenant**. La personale vince sulla
 * condivisa; «Predefinita» toglie la personale.
 */
export function DashboardPersonalizzabile({
  kind, predefinita, gruppi, titolo, sottotitolo, azioni, sopra,
}: {
  kind: DashboardKind
  predefinita: (visibile: (d: DefinizioneWidget) => boolean) => ConsoleDashboard
  /** Quali gruppi del catalogo offrire (vuoto = tutti). */
  gruppi?: string[]
  titolo?: (dash: ConsoleDashboard, modifica: boolean, cambiaNome: (n: string) => void) => ReactNode
  sottotitolo?: (dash: ConsoleDashboard) => ReactNode
  /** Comandi in più nella testata, in lettura. */
  /** Actions next to «Personalizza»; they get the dashboard on screen and the section open now. */
  azioni?: (dash: ConsoleDashboard, sezione: number) => ReactNode
  /** Contenuto sopra la griglia, in lettura (l'onboarding, il sonar). */
  sopra?: ReactNode
}) {
  const { principal } = useAuth()
  const permessi = usePermessiModuli()
  const eagle = useEagle()
  const elenco = useAsync(() => api.dashboards(kind), [kind])
  const [dash, setDash] = useState<ConsoleDashboard | null>(null)
  // La sezione aperta sta nell'URL (`?sezione=`): un collegamento porta alla
  // sezione giusta e il tasto indietro torna a quella di prima.
  const [parametri, setParametri] = useSearchParams()
  const sezione = Math.max(0, Number(parametri.get('sezione') ?? 0) || 0)
  const setSezione = (i: number) => {
    const q = new URLSearchParams(parametri)
    if (i > 0) q.set('sezione', String(i))
    else q.delete('sezione')
    setParametri(q, { replace: true })
  }
  const [modifica, setModifica] = useState(false)
  const [catalogoAperto, setCatalogoAperto] = useState(false)
  // The paired monitors, for «Schermo di riferimento» → «come il monitor
  // X»: only while editing an Eagle dashboard, and only if this person may
  // see them (an operator gets a 403: then the list is just empty).
  const monitorAccoppiati = useAsync(
    () => (modifica && kind !== 'home' ? api.wallMonitors().then((d) => d.monitors).catch(() => []) : Promise.resolve([])),
    [modifica, kind])
  /** Il widget aperto nell'editor (titolo e opzioni), in modifica. */
  const [inModifica, setInModifica] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const [avviso, setAvviso] = useState<string | null>(null)
  const [errore, setErrore] = useState<unknown>(null)
  const [invio, setInvio] = useState(false)

  const visibile = useMemo(() => (d: DefinizioneWidget) =>
    (!d.modulo || permessi.vede(d.modulo)) && (!d.eagle || eagle) && (!gruppi || gruppi.includes(d.gruppo)), [permessi, eagle, gruppi])
  const admin = elenco.data?.admin ?? (principal?.roles.includes('tenant_admin') || principal?.roles.includes('provider_admin') || false)
  const superuser = principal?.roles.includes('provider_admin') ?? false
  // Which dashboard this person opened last (mine or the tenant's): a
  // per-browser convenience, so «Salva per il tenant» followed by a reload
  // shows the tenant's one and not the personal copy.
  const chiaveScelta = `censo.dashboard.${kind}`
  const ricorda = (id: string | null) => {
    try {
      if (id) localStorage.setItem(chiaveScelta, id)
      else localStorage.removeItem(chiaveScelta)
    } catch { /* storage non disponibile: pazienza */ }
  }

  useEffect(() => {
    if (!elenco.data || dash) return
    const mie = elenco.data.dashboards
    let ricordata: string | null = null
    try { ricordata = localStorage.getItem(chiaveScelta) } catch { ricordata = null }
    // `?d=` (from «Gestione cruscotti», from the wall's «Personalizza») opens
    // that one and makes it the remembered one.
    const voluta = parametri.get('d')
    const scelta = mie.find((d) => d.id === voluta) ?? mie.find((d) => d.id === ricordata)
      ?? mie.find((d) => !d.shared) ?? mie.find((d) => d.shared) ?? null
    if (voluta && scelta?.id === voluta) ricorda(voluta)
    setDash(scelta ?? predefinita(visibile))
  }, [elenco.data, dash, visibile, predefinita, chiaveScelta, parametri])

  if (!dash) return <Spinner />
  const sez = dash.sections[Math.min(sezione, dash.sections.length - 1)] ?? dash.sections[0]
  const schermo = leggiSchermo(dash.screen)
  const aggiornaSezione = (parte: Partial<DashboardSezione>) =>
    setDash({ ...dash, sections: dash.sections.map((s) => (s.id === sez.id ? { ...s, ...parte } : s)) })
  const aggiornaWidget = (id: string, parte: Partial<DashboardWidget>) =>
    aggiornaSezione({ widgets: sez.widgets.map((x) => (x.id === id ? { ...x, ...parte } : x)) })

  async function salva(condivisa: boolean) {
    if (!dash) return
    setInvio(true)
    setErrore(null)
    try {
      // The pictures of the image widgets go in `images`, apart (see impacchettaImmagini).
      const corpo = { name: dash.name, kind, shared: condivisa, ...impacchettaImmagini(dash.sections), rotation_s: dash.rotation_s, screen: dash.screen ?? null, locked: condivisa && !!dash.locked }
      // The one to update: the dashboard open (if it already is of that
      // kind), else the existing one of that kind with the same name — «Salva
      // per il tenant» from a personal copy used to create a new tenant
      // dashboard every time, and the list kept showing the first one.
      const tutte = elenco.data?.dashboards ?? []
      const stessoNome = (d: ConsoleDashboard) => d.shared === condivisa && d.name.trim().toLowerCase() === dash.name.trim().toLowerCase()
      const bersaglio = dash.id && dash.shared === condivisa ? dash.id : (tutte.find(stessoNome)?.id ?? null)
      const salvata = bersaglio
        ? await api.dashboardAggiorna(bersaglio, corpo)
        : await api.dashboardCrea(corpo)
      setDash(salvata)
      ricorda(salvata.id)
      setModifica(false)
      setCatalogoAperto(false)
      setAvviso(null)
      await elenco.reload()
    } catch (e) {
      setErrore(e)
    } finally {
      setInvio(false)
    }
  }
  /**
   * La dashboard in un file: sezioni, titoli, misure e opzioni, con i NOMI
   * accanto agli identificativi (ci_name, label, view_name): è coi nomi che
   * si rimappa in un altro tenant, dove gli id non valgono niente.
   */
  function esporta() {
    if (!dash) return
    const corpo = { censo_dashboard: 1, kind, name: dash.name, rotation_s: dash.rotation_s, screen: dash.screen ?? null, sections: dash.sections, exported_at: new Date().toISOString() }
    const blob = new Blob([JSON.stringify(corpo, null, 2)], { type: 'application/json' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    const nome = dash.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'dashboard'
    a.download = `censo-dashboard-${kind}-${nome}.json`
    a.click()
    URL.revokeObjectURL(a.href)
  }

  /**
   * Il file di una dashboard, anche di un altro tenant: i riferimenti ai CI e
   * alle viste di topologia si ritrovano **per nome**; quelli che qui non
   * esistono si svuotano e si contano, così la persona li sistema in «Titolo
   * e opzioni» invece di scoprire widget muti. Niente viene salvato finché
   * non si preme «Salva».
   */
  async function importa(file: File) {
    if (!dash) return
    setErrore(null)
    setAvviso(null)
    try {
      const dati = JSON.parse(await file.text()) as Partial<ConsoleDashboard> & { censo_dashboard?: number }
      if (dati?.censo_dashboard !== 1 || !Array.isArray(dati.sections)) throw new Error(t('Il file non è una dashboard esportata da CENSO.'))
      if (dati.kind && dati.kind !== kind) throw new Error(t('Questa è una dashboard «{a}»: importala nella sua pagina.', { a: dati.kind === 'eagle' ? 'Eagle' : t('Home') }))
      const [cis, viste] = await Promise.all([
        api.eagleCis().then((d) => d.cis).catch(() => []),
        api.topologyViews().catch(() => []),
      ])
      const ciPerNome = new Map(cis.map((c) => [c.name.toLowerCase(), c.ci_id]))
      const vistaPerNome = new Map(viste.map((v) => [v.name.toLowerCase(), v.id]))
      let mancanti = 0
      const ciDi = (nome: unknown, vecchio: unknown): string => {
        const id = ciPerNome.get(String(nome ?? '').toLowerCase())
        if (id) return id
        if (cis.some((c) => c.ci_id === vecchio)) return String(vecchio)
        mancanti++
        return ''
      }
      const rimappa = (s: Record<string, unknown>) => ({ ...s, ci_id: ciDi(s.ci_name, s.ci_id) })
      const sections: DashboardSezione[] = dati.sections.map((sez) => ({
        id: idNuovo(),
        title: String(sez.title ?? ''),
        widgets: (Array.isArray(sez.widgets) ? sez.widgets : []).filter((w) => w && typeof w.type === 'string').map((w) => {
          const o: Record<string, unknown> = { ...(w.options ?? {}) }
          if (o.ci_id != null || o.ci_name != null) o.ci_id = ciDi(o.ci_name, o.ci_id)
          if (Array.isArray(o.serie)) o.serie = (o.serie as Record<string, unknown>[]).map(rimappa)
          if (o.link && typeof o.link === 'object') {
            const l = o.link as { a?: Record<string, unknown> | null; b?: Record<string, unknown> | null }
            o.link = { ...l, a: l.a ? rimappa(l.a) : null, b: l.b ? rimappa(l.b) : null }
          }
          if (o.view_id != null || o.view_name != null) {
            const id = vistaPerNome.get(String(o.view_name ?? '').toLowerCase()) ?? (viste.some((v) => v.id === o.view_id) ? String(o.view_id) : '')
            if (!id) mancanti++
            o.view_id = id
          }
          const size = MISURE.some((m) => m.key === w.size) ? w.size : 'm'
          return { id: idNuovo(), type: w.type, size, tall: !!w.tall, options: o } as DashboardWidget
        }),
      }))
      if (!sections.length) throw new Error(t('La dashboard importata non ha sezioni.'))
      setDash({ ...dash, name: typeof dati.name === 'string' && dati.name ? dati.name : dash.name, rotation_s: Number(dati.rotation_s) || dash.rotation_s,
                screen: leggiSchermo(typeof dati.screen === 'string' ? dati.screen : null) ? String(dati.screen) : (dash.screen ?? null), sections })
      setSezione(0)
      setModifica(true)
      setAvviso(mancanti
        ? t('Dashboard importata: {n} riferimenti (CI o viste) non esistono in questo tenant — sistemali in «Titolo e opzioni», poi salva.', { n: mancanti })
        : t('Dashboard importata: salvala come tua o per il tenant.'))
    } catch (e) {
      setErrore(e)
    }
  }

  // «Nuovo»: an empty dashboard to compose from scratch, with a name of its
  // own — not a copy of the one open. Unsaved until «Salva come mia».
  function nuova() {
    if (modifica && !window.confirm(t('Aprire un cruscotto nuovo e vuoto? Le modifiche non salvate di questo vanno perse.'))) return
    setDash({
      id: null, name: t('Nuovo cruscotto'), kind, shared: false, rotation_s: 30, screen: null, updated_at: null,
      locked: false, can_edit: true, sections: [{ id: idNuovo(), title: t('Sezione 1'), widgets: [] }],
    })
    setSezione(0)
    setInModifica(null)
    setModifica(true)
    setCatalogoAperto(true)
  }

  async function ripristina() {
    if (!dash || !window.confirm(t('Tornare alla dashboard predefinita? La tua personalizzazione va persa.'))) return
    setInvio(true)
    setErrore(null)
    try {
      if (dash.id && !dash.shared) await api.dashboardElimina(dash.id, kind)
      ricorda(null)
      setDash(predefinita(visibile))
      setSezione(0)
      setModifica(false)
      await elenco.reload()
    } catch (e) {
      setErrore(e)
    } finally {
      setInvio(false)
    }
  }

  /** The tenant dashboard open: deleted for everybody (an administrator, not held by someone else's lock). */
  async function eliminaCondivisa() {
    if (!dash?.id || !dash.shared) return
    if (!window.confirm(t('Eliminare la dashboard del tenant «{n}»? Chi la usa tornerà alla predefinita.', { n: dash.name }))) return
    setInvio(true)
    setErrore(null)
    try {
      await api.dashboardElimina(dash.id, kind)
      ricorda(null)
      setDash(null)
      setModifica(false)
      await elenco.reload()
    } catch (e) {
      setErrore(e)
    } finally {
      setInvio(false)
    }
  }
  const scegli = (id: string) => {
    const d = (elenco.data?.dashboards ?? []).find((x) => x.id === id) ?? null
    setDash(d ?? predefinita(visibile))
    ricorda(d?.id ?? null)
    setSezione(0)
  }
  const altre = elenco.data?.dashboards ?? []
  const etichetta = (d: ConsoleDashboard) => `${d.shared ? t('Del tenant') : t('La mia')} · ${d.name}${d.shared && d.created_by_name ? ` (${d.created_by_name})` : ''}${d.locked ? ' 🔒' : ''}`
  const bloccata = !!(dash.id && dash.shared && !dash.can_edit)
  const chiBlocca = dash.created_by_name ?? '?'

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">
            {titolo ? titolo(dash, modifica, (n) => setDash({ ...dash, name: n })) : modifica ? (
              <input value={dash.name} onChange={(e) => setDash({ ...dash, name: e.target.value })} aria-label={t('Nome della dashboard')} style={{ fontSize: 20, fontWeight: 600, maxWidth: 360 }} />
            ) : dash.name}
          </h1>
          <p className="page__subtitle">
            {sottotitolo ? sottotitolo(dash) : dash.shared ? t('Dashboard del tenant') : dash.id ? t('La tua dashboard') : t('Dashboard predefinita: personalizzala e diventa tua.')}
          </p>
          {/* Which dashboard: on the left, under the title. On the right a
              long «Del tenant · …» label pushed the buttons around. */}
          {!modifica && (altre.length > 1 || (altre.length === 1 && !dash.id)) && (
            <select className="dashboard__scelta" aria-label={t('Quale dashboard')} value={dash.id ?? ''} onChange={(e) => scegli(e.target.value)}>
              {!dash.id && <option value="">{t('Predefinita')}</option>}
              {altre.map((d) => <option key={d.id ?? ''} value={d.id ?? ''}>{etichetta(d)}</option>)}
            </select>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {!modifica && (
            <>
              {azioni?.(dash, sezione)}
              <button type="button" className="secondary" onClick={nuova} title={t('Un cruscotto nuovo e vuoto, da comporre dal catalogo')}>{t('Nuovo')}</button>
              <button type="button" onClick={() => setModifica(true)}>{t('Personalizza')}</button>
            </>
          )}
          {modifica && (
            <>
              <button type="button" onClick={() => void salva(false)} disabled={invio}>{invio ? t('Salvo…') : t('Salva come mia')}</button>
              {admin && <button type="button" className="secondary" onClick={() => void salva(true)} disabled={invio || bloccata}
                                title={bloccata ? t('Bloccata da {chi}: solo chi l’ha creata può modificarla. Puoi salvarla come tua.', { chi: chiBlocca }) : undefined}>{t('Salva per il tenant')}</button>}
              {dash.id && dash.shared && dash.can_edit && (
                <button type="button" className="secondary" onClick={() => void eliminaCondivisa()} disabled={invio}>{t('Elimina dal tenant')}</button>
              )}
              <button type="button" className="secondary" onClick={esporta} disabled={invio} title={t('Scarica la dashboard in un file, da importare qui o in un altro tenant')}>{t('Esporta')}</button>
              <button type="button" className="secondary" onClick={() => fileRef.current?.click()} disabled={invio}>{t('Importa…')}</button>
              <input ref={fileRef} type="file" accept="application/json,.json" hidden aria-label={t('File della dashboard')}
                     onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void importa(f) }} />
              <button type="button" className="secondary" onClick={nuova} disabled={invio} title={t('Un cruscotto nuovo e vuoto, da comporre dal catalogo')}>{t('Nuovo')}</button>
              <button type="button" className="secondary" onClick={ripristina} disabled={invio}>{t('Predefinita')}</button>
              <button type="button" className="secondary" onClick={() => { setModifica(false); setCatalogoAperto(false); setDash(null) }} disabled={invio}>{t('Annulla')}</button>
            </>
          )}
        </div>
      </header>
      <ErrorBanner error={errore ?? elenco.error} />
      {modifica && (
        <p className="muted dashboard__proprieta">
          {!dash.id
            ? t('Non ancora salvata: «Salva come mia» la fa tua, «Salva per il tenant» la dà a tutti (solo un amministratore).')
            : dash.shared
              ? <>
                  {t('Dashboard del tenant «{n}»', { n: dash.name })}
                  {dash.created_by_name && <> · {t('creata da {chi} il {quando}', { chi: dash.created_by_name, quando: formatDateTime(dash.created_at ?? null) })}</>}
                  {dash.updated_by_name && <> · {t('ultima modifica di {chi} il {quando}', { chi: dash.updated_by_name, quando: formatDateTime(dash.updated_at) })}</>}
                  {bloccata && <> · <strong>{t('bloccata da {chi}: solo chi l’ha creata può modificarla', { chi: chiBlocca })}</strong></>}
                </>
              : <>
                  {t('La tua dashboard «{n}»', { n: dash.name })}
                  {dash.created_at && <> · {t('creata il {quando}', { quando: formatDateTime(dash.created_at) })}</>}
                  {dash.updated_at && <> · {t('ultima modifica il {quando}', { quando: formatDateTime(dash.updated_at) })}</>}
                </>}
          {admin && (!dash.id || (dash.shared && (dash.is_creator || superuser))) && (
            <label className="eagle-interruttore" style={{ marginLeft: 12 }}>
              <input type="checkbox" checked={!!dash.locked} onChange={(e) => setDash({ ...dash, locked: e.target.checked })} />{' '}
              {t('Blocca le modifiche: solo chi l’ha creata può modificarla (vale quando è salvata per il tenant)')}
            </label>
          )}
        </p>
      )}
      {avviso && <p className="muted" style={{ margin: '0 0 10px' }}>{avviso}</p>}
      {!modifica && sopra}

      <div className="dashboard__barra">
        <div className="dashboard__sezioni" role="tablist">
          {dash.sections.map((s, i) => (
            <button key={s.id} type="button" role="tab" aria-selected={s.id === sez?.id}
                    className={`dashboard__sezione${s.id === sez?.id ? ' dashboard__sezione--on' : ''}`} onClick={() => setSezione(i)}>
              {/* Il titolo si traduce al render: quello salvato è la frase sorgente
                  (italiana) delle sezioni predefinite; un titolo personalizzato non
                  è nel dizionario e t() lo restituisce invariato. */}
              {t(s.title)}
            </button>
          ))}
          {modifica && (
            <button type="button" className="dashboard__sezione" onClick={() => {
              const nuova: DashboardSezione = { id: idNuovo(), title: t('Nuova sezione'), widgets: [] }
              setDash({ ...dash, sections: [...dash.sections, nuova] })
              setSezione(dash.sections.length)
            }}>{t('+ sezione')}</button>
          )}
        </div>
        {modifica && sez && (
          <>
            <input value={sez.title} onChange={(e) => aggiornaSezione({ title: e.target.value })} aria-label={t('Titolo della sezione')} style={{ width: 200 }} />
            <button type="button" className="secondary" onClick={() => setCatalogoAperto(!catalogoAperto)}>{t('Aggiungi widget')}</button>
            {dash.sections.length > 1 && (
              <button type="button" className="linklike" onClick={() => {
                if (!window.confirm(t('Togliere la sezione «{s}» con i suoi widget?', { s: sez.title }))) return
                setDash({ ...dash, sections: dash.sections.filter((s) => s.id !== sez.id) })
                setSezione(0)
              }}>{t('togli sezione')}</button>
            )}
            <label className="checkbox eagle-piccolo" title={t('Una matrice di 24 colonne per 12 righe (uno schermo): ogni widget si sposta e si ridimensiona a mano, anche sopra un altro.')}>
              <input type="checkbox" checked={sez.layout === 'free'}
                     onChange={(e) => aggiornaSezione(e.target.checked
                       ? { layout: 'free', widgets: completaGriglia(sez.widgets) }
                       : { layout: 'auto' })} />
              {' '}{t('Disposizione libera')}
            </label>
            <SceltaSchermo valore={dash.screen} onCambia={(v) => setDash({ ...dash, screen: v })} monitor={monitorAccoppiati.data ?? []} />
            <label className="muted eagle-piccolo" style={{ marginLeft: 'auto' }}>
              {t('Sinottico: cambia sezione ogni')}{' '}
              <input type="number" min={0} max={600} value={dash.rotation_s} onChange={(e) => setDash({ ...dash, rotation_s: Number(e.target.value) })} style={{ width: 70 }} /> s
            </label>
          </>
        )}
      </div>

      {modifica && catalogoAperto && sez && (
        <Card title={t('Catalogo dei widget')}>
          {GRUPPI_WIDGET.filter((g) => !gruppi || gruppi.includes(g.key)).map((g) => {
            const voci = CATALOGO.filter((d) => d.gruppo === g.key && visibile(d))
            if (!voci.length) return null
            return (
              <div key={g.key} style={{ marginBottom: 12 }}>
                <h4 style={{ margin: '0 0 6px' }}>{g.label()}</h4>
                <div className="dashboard__catalogo">
                  {voci.map((d) => (
                    <button key={d.tipo} type="button" className="dashboard__voce"
                            onClick={() => aggiornaSezione({ widgets: [...sez.widgets, widgetDa(d)] })}>
                      <strong>{d.label()}</strong><span>{d.descrizione()}</span>
                    </button>
                  ))}
                </div>
              </div>
            )
          })}
        </Card>
      )}

      {sez && (sez.widgets.length === 0 ? (
        <p className="muted">{modifica ? t('Sezione vuota: aggiungi un widget dal catalogo.') : t('Questa sezione è vuota.')}</p>
      ) : modifica && schermo ? (
        // The reference screen: the grid at the monitor's size, scaled to the
        // page, with the wall's header and the bottom edge of the screen.
        <CorniceSchermo schermo={schermo}>
          {(scala) => (
            <Griglia<DashboardWidget> widgets={sez.widgets} modifica libero={sez.layout === 'free'} scala={scala}
                     altezzaRiga={sez.layout === 'free' ? altezzaRigaPer(schermo) : undefined}
                     onCambia={(widgets) => aggiornaSezione({ widgets })}
                     disegna={(w) => (
                       <>
                         <div className="griglia__strumenti" style={{ marginTop: -2 }}>
                           <button type="button" className="linklike" onClick={() => setInModifica(w.id)}>{t('Titolo e opzioni…')}</button>
                           {typeof w.options?.title === 'string' && w.options.title.trim() !== '' && (
                             <span className="muted">«{String(w.options.title)}»</span>
                           )}
                         </div>
                         <DisegnaWidget tipo={w.type} options={w.options ?? {}} sinottico />
                       </>
                     )} />
          )}
        </CorniceSchermo>
      ) : (
        <Griglia<DashboardWidget> widgets={sez.widgets} modifica={modifica} libero={sez.layout === 'free'}
                 onCambia={(widgets) => aggiornaSezione({ widgets })}
                 disegna={(w) => (
                   <>
                     {modifica && (
                       <div className="griglia__strumenti" style={{ marginTop: -2 }}>
                         <button type="button" className="linklike" onClick={() => setInModifica(w.id)}>{t('Titolo e opzioni…')}</button>
                         {typeof w.options?.title === 'string' && w.options.title.trim() !== '' && (
                           <span className="muted">«{String(w.options.title)}»</span>
                         )}
                       </div>
                     )}
                     <DisegnaWidget tipo={w.type} options={w.options ?? {}} sinottico={false} />
                   </>
                 )} />
      ))}
      {modifica && inModifica && sez && (() => {
        const w = sez.widgets.find((x) => x.id === inModifica)
        const def = w ? PER_TIPO[w.type] : undefined
        if (!w || !def) return null
        return (
          <EditorWidget widget={w} definizione={def} onChiudi={() => setInModifica(null)}
                        onSalva={(options) => { aggiornaWidget(w.id, { options }); setInModifica(null) }} />
        )
      })()}
    </>
  )
}
